import React from 'react';
import { Bell, AlertTriangle, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSystemAlerts } from '@/hooks/useSystemAlerts';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const SystemAlertsBell: React.FC = () => {
  const { user } = useAuth();
  const { alerts, criticalCount, warningCount, totalCount, acknowledge } = useSystemAlerts();
  const activeAlerts = alerts.filter((a) => a.is_active);

  if (user?.role !== 'admin') return null;

  const badgeColor =
    criticalCount > 0 ? 'bg-destructive' : warningCount > 0 ? 'bg-yellow-500' : '';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-md hover:bg-muted/80 relative"
          title="Alertas do sistema"
        >
          <Bell className="h-4 w-4 text-foreground" />
          {totalCount > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 h-4 min-w-[1rem] px-1 rounded-full ${badgeColor} text-[10px] font-bold text-white flex items-center justify-center`}
            >
              {totalCount > 9 ? '9+' : totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-3 border-b border-border/40">
          <h3 className="font-medium text-sm">Alertas do sistema</h3>
          <p className="text-xs text-muted-foreground">
            {totalCount === 0
              ? 'Nenhum alerta ativo'
              : `${criticalCount} crítico(s), ${warningCount} aviso(s)`}
          </p>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {activeAlerts.length === 0 ? (
            <div className="p-6 flex flex-col items-center gap-2 text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <p className="text-sm">Tudo funcionando normalmente</p>
            </div>
          ) : (
            activeAlerts.slice(0, 10).map((alert) => (
              <div
                key={alert.id}
                className="p-3 border-b border-border/40 last:border-0 hover:bg-muted/30"
              >
                <div className="flex items-start gap-2">
                  {alert.severity === 'critical' ? (
                    <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{alert.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{alert.message}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(alert.created_at), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </span>
                      {!alert.acknowledged_at && (
                        <button
                          type="button"
                          onClick={() => acknowledge(alert.id)}
                          className="text-[11px] text-primary hover:underline"
                        >
                          Reconhecer
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SystemAlertsBell;
